<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ActionResult from '@/components/ActionResult.vue';
import FormSectionBankCard from '@/components/FormSectionBankCard.vue';
import DynamicAmount from '@/components/DynamicAmount.vue';
import PaymentAreaWarning from '@/components/PaymentAreaWarning.vue';
import geInstructionLinkByPlatform from '@/helpers/geInstructionLinkByPlatform';
import GooglePay from '@/components/GooglePay.vue';
import ApplePay from '@/components/ApplePay.vue';
import FormSectionBankAccount from '@/components/FormSectionBankAccount.vue';
import Recurring from '@/components/Recurring.vue';
import Paypal from '@/components/Paypal.vue';
import InvoiceCustomFields from '@/components/InvoiceCustomFields.vue';

function getRegexp(value) {
  return new RegExp(value);
}

function regEx() {
}

export default {
  name: 'FormSection',
  type: 'bank_card',
  components: {
    Recurring,
    FormSectionBankAccount,
    ApplePay,
    ActionResult,
    FormSectionBankCard,
    PaymentAreaWarning,
    GooglePay,
    DynamicAmount,
    Paypal,
    InvoiceCustomFields,
  },

  props: {
    isPageView: {
      type: Boolean,
      default: false,
    },
    isOneLine: {
      type: Boolean,
      default: false,
    },
  },

  filters: {
    getRegexp,
  },

  data() {
    return {
      regEx: '^(?:4[0-9]{12}(?:[0-9]{3})?|[25][1-7][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\\d{3})\\d{11})|62[0-9]{14,17}$',
      paymentMethodGroups: {
        BANKCARD: {
          iconComponent: 'IconCard',
        },
        NETELLER: {
          iconComponent: 'IconCard',
        },
        ALIPAY: {
          iconComponent: 'IconCard',
        },
        BITCOIN: {
          iconComponent: 'IconCard',
        },
        QIWI: {
          iconComponent: 'IconQiwi',
        },
        WEBMONEY: {
          iconComponent: 'IconWebmoney',
        },
      },
      paymentMethodTypes: {
        ewallet: {
          iconComponent: 'IconCard',
        },
      },
      selectedPaymentMethod: null,
      dynamicAmount: 0,
      dynamicAmountInput: 0,
      baseAmount: 0,
    };
  },

  computed: {
    ...mapState('PaymentForm', [
      'orderData',
      'orderParams',
      'paymentData',
      'customFieldsPayload',
      'paymentACHData',
      'cards',
      'actionResult',
      'userIpGeoData',
      'isEmailFieldExposed',
      'isCountryFieldExposed',
      'isZipFieldExposed',
      'isZipInvalid',
      'currentPlatformId',
      'type',
    ]),
    ...mapGetters('PaymentForm', [
      'activePaymentMethod',
      'isPaymentFailed',
      'isPaymentSuccess',
      // Todo: remove after #195691
      'hasExpAutofill',
    ]),
    ...mapGetters('PaymentForm/Redirect', [
      'isRedirect',
      'isAutoRedirect',
      'redirectDelay',
      'hasTimer',
      'redirectSuccessUrl',
      'redirectFailUrl',
    ]),

    hasBankCardPayment() {
      return (this.orderData.payment_type.indexOf('card') !== -1) || (!this.hasBankAccountPayment);
    },

    hasBankAccountPayment() {
      return this.orderData.payment_type.indexOf('ach') !== -1;
    },

    isDynamicAmount() {
      return this.orderData.dynamic_amount;
    },

    canBeRecurring() {
      return this.orderData.can_be_recurring ?? false;
    },

    isPaymentFormVisible() {
      if (this.actionResult) {
        return false;
      }

      return true;
    },

    isSubmitButtonDisabled() {
      return false;
    },

    orderType() {
      return this.orderData.type;
    },

    platformInstructionLink() {
      return geInstructionLinkByPlatform(this.currentPlatformId);
    },

    isPaymentSuccess() {
      return this.actionResult
        && (this.actionResult.type === 'success' || this.actionResult.type === 'ach_success');
    },

    isPaymentFailed() {
      return this.actionResult
        && this.actionResult.type !== 'success'
        && this.actionResult.type !== 'ach_success';
    },

    isPayEnabled() {
      return this.orderData.merchant.pay_enabled;
    },

    payApiKey() {
      return this.orderData.merchant.pay_api_key;
    },

    isCvvOptional() {
      return this.orderData.merchant.cvv_optional;
    },
    isAddressOptional() {
      return this.orderData.merchant.address_optional;
    },
    statementDesc() {
      return this.orderData.merchant.statement_description;
    },
    surcharge() {
      return this.orderData.merchant.surcharge;
    },
    surchargePercentage() {
      return this.orderData.surcharge_percentage;
    },
    name() {
      return this.orderData.merchant.name;
    },
    walletPayButtonsEnabled() {
      return this.isPaymentFormVisible && this.hasBankCardPayment && this.selectedPaymentMethod === 'Card';
    },
  },
  mounted() {
    const recaptchaScript = document.createElement('script');
    recaptchaScript.setAttribute(
      'src',
      'https://pay.google.com/gp/p/js/pay.js',
    );
    recaptchaScript.async = true;
    document.head.appendChild(recaptchaScript);
    this.selectedPaymentMethod = this.hasBankCardPayment ? 'Card' : 'Bank Account';
    this.baseAmount = this.orderData.merchant.billing_amount;
    console.log(this.orderData);
  },

  watch: {
    cards: {
      handler(value) {
        if (!value.length) {
          this.setPaymentData({ cardDataType: 'manual' });
        }
      },
      immediate: true,
    },
  },

  validations() {
    return {};
  },

  methods: {
    ...mapActions('PaymentForm', [
      'createPayment',
      'createACHPayment',
      'setPaymentData',
      'setPaymentACHData',
      'submitUserCountry',
      'createGooglePayment',
      'createApplePayment',
      'createPaypalPayment',
      'createErrorPaypalPayment',
      'setCustomFieldsPayload',
    ]),

    async handleMainButtonClick() {
      if (this.isPaymentFailed) {
        this.$emit('close');
      } else if (this.isPaymentSuccess) {
        this.$emit('close');
      } else if (this.isPaymentFormVisible && !this.paymentSubmitted) {
        this.paymentSubmitted = true;
        await this.submitPaymentForm();
        this.paymentSubmitted = false;
      }
    },

    handleDynamicAmountChange(value) {
      this.dynamicAmountInput = value;
      this.refreshAmountData();
    },

    handleRecurringChange(value) {
      this.orderData.recurring = value;
    },

    handleTabChange(tabIndex, newTab) {
      this.selectedPaymentMethod = newTab.tabId;
      this.refreshAmountData();
    },

    async submitPaymentForm() {
      this.$v.$touch();

      const isValidArray = [
        !this.$v.$invalid,
      ];

      if (this.isDynamicAmount) {
        const { isValid } = this.$refs.dynamicAmountForm.validate();
        isValidArray.push(isValid);
      }

      if (this.canBeRecurring) {
        const { isValid } = this.$refs.recurringForm.validate();
        isValidArray.push(isValid);
      }

      if (this.selectedPaymentMethod === 'Bank Account') {
        const { isValid } = this.$refs.bankAccountForm.validate();
        isValidArray.push(isValid);
      } else {
        const { isValid } = this.$refs.bankCardForm.validate();
        isValidArray.push(isValid);
      }
      if (this.orderData.merchant.custom_fields && this.orderData.merchant.custom_fields.length) {
        const { isValid } = this.$refs.InvoiceCustomFieldsForm.validate();
        isValidArray.push(isValid);
      }
      if (isValidArray.filter(item => !item).length) {
        // has errors
        return;
      }

      if (this.selectedPaymentMethod === 'Bank Account') {
        await this.createACHPayment({
          ...this.paymentACHData,
        });
      } else {
        await this.createPayment({
          ...this.paymentData,
        });
      }
    },

    setNewUserCountry(value) {
      this.setPaymentData({
        country: value,
        city: '',
        zip: '',
      });
    },

    refreshAmountData() {
      if (this.isDynamicAmount) {
        this.dynamicAmount = Math.trunc(this.dynamicAmountInput * 100);
        this.baseAmount = this.dynamicAmount;
      }

      if (this.selectedPaymentMethod !== 'Bank Account') {
        this.orderData.merchant.surcharge = Math.round(this.surchargePercentage ? this.baseAmount * this.surchargePercentage / 100 : 0);
      } else {
        this.orderData.merchant.surcharge = 0;
      }
      this.orderData.charge_amount = this.baseAmount + this.orderData.merchant.surcharge;
    },

    setThreedsPaymentData(val1, val2, val3) {
      this.setPaymentData({
        val1,
        val2,
        val3,
      });
    },

    printReceipt() {
      const restorepage = document.body.innerHTML;
      const printcontent = document.getElementById('printable').innerHTML;
      document.body.innerHTML = printcontent;
      window.print();
      document.body.innerHTML = restorepage;
    },

    googlePay(paymentData) {
      const info = paymentData?.paymentMethodData?.info?.billingAddress || {};
      const token = paymentData?.paymentMethodData?.tokenizationData?.token || '';

      const res = {
        order_id: this.orderData.id,
        amount: this.orderData.charge_amount,
        name: info.name,
        address_line1: info.address1,
        state_code: info.administrativeArea,
        city: info.locality,
        country_code: info.countryCode,
        zip: info.postalCode,
        google_encrypted_data: Buffer.from(JSON.stringify(token), 'utf-8')
          .toString('base64'),
      };

      this.createGooglePayment(res);
    },
    paypalPay() {
      const data = {
        order_id: this.orderData.id,
        token: this.orderParams.orderToken,
      };
      this.createPaypalPayment(data);
    },
    paypalError() {
      this.createErrorPaypalPayment();
    },

    applePay(data) {
      const { paymentData } = data;
      const billingAddress = paymentData.shippingContact;
      const res = {
        order_id: this.orderData.id,
        amount: this.orderData.charge_amount,
        name: `${billingAddress.givenName} ${billingAddress.familyName}`,
        address_line1: billingAddress.addressLines[0],
        city: billingAddress.locality,
        country_code: billingAddress.countryCode,
        zip: billingAddress.postalCode,
        apple_encrypted_data: Buffer.from(JSON.stringify(paymentData.token), 'utf-8')
          .toString('base64'),
      };

      this.createApplePayment({
        res,
        callback: data.callback,
      });
    },
  },
};
</script>

<template>
  <form
    id="billing-form"
    :class="[$style.formSection, { [$style[`_isPage`]]: isPageView }]"
    action=""
    @submit.prevent="handleMainButtonClick"
    method="post"
    autocomplete="off"
  >
    <div :class="$style.content">
      <component
        :is="isPageView ? 'div' : 'UiScrollbarBox'"
        :class="$style.scrollbox"
        :settings="isPageView ? undefined : { suppressScrollX: true }"
      >
        <!--
          It's important not to render the form until the requested country is selected
          Otherwise rendering saved cards will cause buggy behavior
          (it's emitting "select" on created)
           -->
        <div
          v-if="isPaymentFormVisible"
          :class="$style.contentInner"
        >

          <h2>{{ orderData.description }}</h2>

          <DynamicAmount
            ref="dynamicAmountForm"
            :is-dynamic-amount="isDynamicAmount"
            @change="handleDynamicAmountChange($event)"
          />
          <div>
            <h4 class="textColor">Pay with</h4>
          </div>
          <vue-tabs @tab-change="handleTabChange">
            <v-tab
              title="Card"
              v-if="hasBankCardPayment"
            >
              <FormSectionBankCard
                ref="bankCardForm"
                :value="paymentData"
                :cards="cards"
                :cardNumberValidator="regEx | getRegexp"
                :isEmailFieldExposed="isEmailFieldExposed"
                :isCountryFieldExposed="isCountryFieldExposed"
                :isZipFieldExposed="isZipFieldExposed"
                :isZipInvalid="isZipInvalid"
                :isPayEnabled="isPayEnabled"
                :merchantPayApiKey="payApiKey"
                :isCvvOptional="isCvvOptional"
                :isAddressOptional="isAddressOptional"
                :statementDesc="statementDesc"
                :surcharge="surcharge"
                :dynamicAmount="isDynamicAmount"
                :surchargePercentage="surchargePercentage"
                :name="name"
                @countryChange="setNewUserCountry($event)"
                @change="setPaymentData($event)"
              />
            </v-tab>
            <v-tab
              title="Bank Account"
              v-if="hasBankAccountPayment"
            >
              <FormSectionBankAccount
                ref="bankAccountForm"
                :value="paymentACHData"
                :surcharge="surcharge"
                :dbaName="name"
                @countryChange="setNewUserCountry($event)"
                @change="setPaymentACHData($event)"
              />
            </v-tab>
          </vue-tabs>
          <InvoiceCustomFields v-if="this.orderData.merchant.custom_fields &&
           this.orderData.merchant.custom_fields.length"
                               ref="InvoiceCustomFieldsForm"
                   :custom-fields="this.orderData.merchant.custom_fields"
                   @update:innerCustomFields="setCustomFieldsPayload($event)"
          />
            <Recurring
                    ref="recurringForm"
                    :canBeRecurring="canBeRecurring"
                    @change="handleRecurringChange($event)"
            />
        </div>
        <div
          :class="$style.contentInner"
          v-if="!isPaymentFormVisible"
        >
          <ActionResult
            v-if="actionResult"
            :email="paymentData.email"
            :code="actionResult.code"
            :orderId="orderData.id"
            :type="actionResult.type"
            :platformInstructionLink="platformInstructionLink"
            id="printable"
          />
        </div>
      </component>
    </div>
    <div :class="$style.footer">
      <input hidden type="text" name="x_amount" v-bind:value="orderData.charge_amount"
             data-threeds="amount"/>
      <div :class="isPaymentSuccess ? $style.textpaymentsuccessfooter : $style.textfooter">
        <p id="blinktext" class="blink blink-one">Please Wait! Retrieving 3DS information.</p>
        <div v-if="!this.$store.state.printReceiptLoading">
          <UiButton
            :class="$style.payBtn"
            @click="printReceipt"
          >
            <template>
              Print Receipt
            </template>
          </UiButton>
        </div>
        <div :style="!this.$store.state.printReceiptLoading ? 'width: 30%;' : 'width: 100%;'">
          <Paypal v-if="walletPayButtonsEnabled && !!orderData.merchant.paypal_status"
                  :paypalStatus="orderData.merchant.paypal_status" :baseAmount="baseAmount"
                  :merchantId="orderData.merchant.paypal_id"
                  :token="orderParams.orderToken" :orderId="orderData.id"
                  @paypal-payment-success="paypalPay"
                  @paypal-payment-error="paypalError"
          ></Paypal>
          <GooglePay
            v-if="walletPayButtonsEnabled"
            :merchant="orderData.merchant"
            :totalPrice="orderData.charge_amount/100"
            @google_payload="googlePay"
          />
          <ApplePay
            v-if="walletPayButtonsEnabled"
            :merchant="orderData.merchant"
            :totalPrice="orderData.charge_amount/100"
            @apple_payload="applePay"
          />
          <UiButton
            :class="$style.payBtn"
            :hasBorderRadius="isPageView"
            :disabled="isSubmitButtonDisabled"
            tabindex="10"
            id="paybtn"
          >
            <template v-if="isPaymentFormVisible">
              <IconLock slot="before"/>
              <span>
              {{ $t('FormSection.payButtonPrefix') }}
            </span>
              <span :title="$getPrice(orderData.charge_amount, 'USD')">
               {{ $getPrice(orderData.charge_amount, 'USD') }}
            </span>
            </template>
            <template v-if="isPaymentSuccess">
              {{ $t('FormSection.ok') }}
            </template>
            <template v-if="isPaymentFailed">
              {{ $t('FormSection.close') }}
            </template>
          </UiButton>
        </div>
      </div>
    </div>
  </form>
</template>

<style module lang="scss">

@media print {
  #printable {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }
  html, body {
    height: 100%;
    width: 100%;
  }
}

.tabsHeader {
  text-decoration: none;
}

.formSection {
  display: flex;
  flex-wrap: nowrap;
  flex-direction: column;
  align-content: space-between;
  position: relative;
  min-height: 454px;
  max-height: 100%;
  width: 100%;
}

.formSection._isPage h4 {
  color: #000;
}

.content {
  display: flex;
  flex-wrap: wrap;
  flex-grow: 1;
  flex-direction: column;
  align-content: flex-start;
  position: relative;
  width: 100%;
  max-height: calc(100% - 70px);
}

.scrollbox {
  width: 100%;
  height: 100%;
  flex-grow: 1;
}

.contentInner {
  width: 100%;
  height: 100%;
  flex-grow: 1;
  padding: 0 20px 20px;

  .formSection._isPage & {
    padding: 20px 0px 20px;

    @media screen and (min-width: 640px) {
      padding: 60px 0;
    }
  }
}

.formItem {
  display: flex;
  justify-content: space-between;
}

.footer {
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  width: 100%;

  .formSection._isPage &._sticky {
    left: 0 !important;
    width: 100vw !important;
  }
}

.textpaymentsuccessfooter {
  display: flex;
  width: 100%;
  text-align: center;
  justify-content: space-between;
}

.textfooter {
  display: block;
  width: 100%;
  text-align: center;
}

.payBtn {
  width: 100%;
  transition: border-radius 0.2s ease-out;
  overflow: hidden;
  text-overflow: ellipsis;
}

</style>

<style lang="scss">
.nav.nav-tabs {
  display: flex;
  width: 50%;
  position: relative;
  li {
    flex: 1 0 calc(100% - 10px);
    &:first-of-type {
      margin-right: 10px;
    }
    a {
      text-decoration: none;
      display: flex;
      justify-content: center;
      align-items: center;
      text-align: center;
      font-family: inherit;
      font-size: 18px;
      font-weight: 700;
      color: #fff;
      background-color: #aeb6c4;
      height: 50px;
      white-space: nowrap;
      border-radius: 5px;
      &.active_tab {
        background-color: #5b88de;
      }
      span {

      }
    }
  }

}
</style>
